export type WarehouseEndpoint = {
  id: string
  name: string
  apiBaseUrl: string
  apiPathPrefix: string
  latitude: number | null
  longitude: number | null
}

const SELECTED_WAREHOUSE_KEY = 'nurm_selected_warehouse_v1'
const GEO_QUERY_PARAM = 'geo'
const LEGACY_WAREHOUSE_QUERY_PARAM = 'warehouse'
const DEFAULT_WAREHOUSE_NAME = import.meta.env.VITE_DEFAULT_CITY_NAME ?? 'Москва'
const WAREHOUSE_ENDPOINTS_ENV = import.meta.env.VITE_BACKEND_ENDPOINTS
const DEFAULT_API_PATH_PREFIX = import.meta.env.VITE_API_PATH_PREFIX ?? '/api'
export const IS_WAREHOUSE_SELECTION_MODAL_ENABLED =
  import.meta.env.VITE_WAREHOUSE_SELECTION_MODAL_ENABLED !== '0'

const normalizeApiBaseUrl = (value: string | undefined | null): string => {
  const trimmed = String(value ?? '').trim()
  if (!trimmed) return ''

  try {
    const url = new URL(trimmed)
    url.hostname = url.hostname.replace(/^www\./, '')
    return url.toString().replace(/\/$/, '')
  } catch {
    return trimmed.replace(/\/$/, '')
  }
}

const getNormalizedOrigin = (value: string): string | null => {
  try {
    const url = new URL(value)
    url.hostname = url.hostname.replace(/^www\./, '')
    return url.origin.replace(/\/$/, '')
  } catch {
    return null
  }
}

const toNullableNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number.parseFloat(value)
    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

const normalizeApiPathPrefix = (value: string | undefined | null): string => {
  const trimmed = String(value ?? '').trim()

  if (!trimmed) {
    return '/api'
  }

  return `/${trimmed.replace(/^\/+|\/+$/g, '')}`
}

const DEFAULT_API_BASE_URL = normalizeApiBaseUrl(import.meta.env.VITE_API_BASE_URL)
const CUSTOMER_API_BASE_URL = normalizeApiBaseUrl(
  import.meta.env.VITE_CUSTOMER_API_BASE_URL ?? import.meta.env.VITE_API_BASE_URL
)
const CUSTOMER_API_PATH_PREFIX = normalizeApiPathPrefix(
  import.meta.env.VITE_CUSTOMER_API_PATH_PREFIX ?? DEFAULT_API_PATH_PREFIX
)

const normalizeWarehouseId = (value: string | undefined | null, fallbackIndex: number): string => {
  const trimmed = String(value ?? '').trim().toLowerCase()

  if (!trimmed) {
    return `warehouse-${fallbackIndex + 1}`
  }

  return trimmed
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-_а-яё]/gi, '')
}

export const getWarehouseIdFromSearchParams = (
  params: URLSearchParams
): string | null => {
  const value = params.get(GEO_QUERY_PARAM) ?? params.get(LEGACY_WAREHOUSE_QUERY_PARAM)

  if (!value) {
    return null
  }

  return normalizeWarehouseId(value, 0)
}

const getWarehouseIdFromSearch = (): string | null => {
  if (typeof window === 'undefined') {
    return null
  }

  return getWarehouseIdFromSearchParams(new URLSearchParams(window.location.search))
}

const buildFallbackWarehouse = (): WarehouseEndpoint => ({
  id: 'default',
  name: DEFAULT_WAREHOUSE_NAME,
  apiBaseUrl: DEFAULT_API_BASE_URL,
  apiPathPrefix: normalizeApiPathPrefix(DEFAULT_API_PATH_PREFIX),
  latitude: toNullableNumber(import.meta.env.VITE_DEFAULT_CITY_LATITUDE),
  longitude: toNullableNumber(import.meta.env.VITE_DEFAULT_CITY_LONGITUDE),
})

const parseWarehouseEndpoints = (): WarehouseEndpoint[] => {
  const fallbackWarehouse = buildFallbackWarehouse()

  if (!WAREHOUSE_ENDPOINTS_ENV) {
    return [fallbackWarehouse]
  }

  try {
    const parsed = JSON.parse(WAREHOUSE_ENDPOINTS_ENV) as unknown

    if (!Array.isArray(parsed)) {
      return [fallbackWarehouse]
    }

    const normalized = parsed
      .map((item, index): WarehouseEndpoint | null => {
        if (!item || typeof item !== 'object') {
          return null
        }

        const record = item as Record<string, unknown>
        const apiBaseUrl = normalizeApiBaseUrl(
          typeof record.apiBaseUrl === 'string'
            ? record.apiBaseUrl
            : typeof record.url === 'string'
              ? record.url
              : null
        )

        if (!apiBaseUrl) {
          return null
        }

        const name =
          typeof record.name === 'string' && record.name.trim()
            ? record.name.trim()
            : typeof record.label === 'string' && record.label.trim()
              ? record.label.trim()
              : `Склад ${index + 1}`

        return {
          id: normalizeWarehouseId(
            typeof record.id === 'string' ? record.id : name,
            index
          ),
          name,
          apiBaseUrl,
          apiPathPrefix: normalizeApiPathPrefix(
            typeof record.apiPathPrefix === 'string'
              ? record.apiPathPrefix
              : typeof record.apiPrefix === 'string'
                ? record.apiPrefix
                : DEFAULT_API_PATH_PREFIX
          ),
          latitude: toNullableNumber(record.latitude),
          longitude: toNullableNumber(record.longitude),
        }
      })
      .filter((item): item is WarehouseEndpoint => item !== null)

    return normalized.length > 0 ? normalized : [fallbackWarehouse]
  } catch {
    return [fallbackWarehouse]
  }
}

export const WAREHOUSE_ENDPOINTS = parseWarehouseEndpoints()

export const getDefaultWarehouse = (): WarehouseEndpoint => WAREHOUSE_ENDPOINTS[0]

export const getWarehouseById = (
  warehouseId: string | null | undefined
): WarehouseEndpoint | null => {
  if (!warehouseId) return null
  return WAREHOUSE_ENDPOINTS.find((warehouse) => warehouse.id === warehouseId) ?? null
}

export const getWarehouseByOrigin = (
  origin: string | null | undefined
): WarehouseEndpoint | null => {
  const normalizedOrigin = origin ? getNormalizedOrigin(origin) : null

  if (!normalizedOrigin) {
    return null
  }

  return (
    WAREHOUSE_ENDPOINTS.find(
      (warehouse) => getNormalizedOrigin(warehouse.apiBaseUrl) === normalizedOrigin
    ) ?? null
  )
}

export const getStoredWarehouseId = (): string | null => {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(SELECTED_WAREHOUSE_KEY)
}

export const hasStoredWarehouseSelection = (): boolean => {
  return Boolean(getStoredWarehouseId())
}

export const persistWarehouseSelection = (warehouseId: string): void => {
  if (typeof window === 'undefined') return
  localStorage.setItem(SELECTED_WAREHOUSE_KEY, warehouseId)
}

export const getCurrentWarehouse = (): WarehouseEndpoint => {
  if (typeof window !== 'undefined') {
    const warehouseFromSearch = getWarehouseById(getWarehouseIdFromSearch())
    if (warehouseFromSearch) {
      return warehouseFromSearch
    }

    const warehouseFromHost = getWarehouseByOrigin(window.location.origin)
    const defaultWarehouse = getDefaultWarehouse()

    // Dedicated warehouse domains like spb.nurm.store must force their own backend.
    // On the main domain, allow the user's stored city selection to override the default host.
    if (warehouseFromHost && warehouseFromHost.id !== defaultWarehouse.id) {
      return warehouseFromHost
    }

    const storedWarehouse = getWarehouseById(getStoredWarehouseId())
    if (storedWarehouse) {
      return storedWarehouse
    }

    if (warehouseFromHost) {
      return warehouseFromHost
    }
  }

  const storedWarehouse = getWarehouseById(getStoredWarehouseId())
  return storedWarehouse ?? getDefaultWarehouse()
}

export const getCurrentWarehouseId = (): string => getCurrentWarehouse().id

export const getCurrentApiBaseUrl = (): string => getCurrentWarehouse().apiBaseUrl

export const getCurrentApiPathPrefix = (): string => getCurrentWarehouse().apiPathPrefix

export const getCustomerApiBaseUrl = (): string => CUSTOMER_API_BASE_URL || getCurrentApiBaseUrl()

export const getCustomerApiPathPrefix = (): string => CUSTOMER_API_PATH_PREFIX

export const resolveCustomerApiPath = (path: string): string => {
  if (!path || /^https?:\/\//i.test(path)) {
    return path
  }

  if (path === '/api' || path.startsWith('/api/')) {
    return path.replace(/^\/api(?=\/|$)/, getCustomerApiPathPrefix())
  }

  return path
}

export const resolveApiPath = (path: string): string => {
  if (!path || /^https?:\/\//i.test(path)) {
    return path
  }

  const apiPathPrefix = getCurrentApiPathPrefix()

  if (path === '/api' || path.startsWith('/api/')) {
    return path.replace(/^\/api(?=\/|$)/, apiPathPrefix)
  }

  return path
}

export const getApiOrigin = (): string => {
  const apiBaseUrl = getCurrentApiBaseUrl()
  if (apiBaseUrl) return apiBaseUrl
  if (typeof window === 'undefined') return ''
  return window.location.origin.replace(/\/$/, '')
}

export const resolveApiUrl = (path: string): string => {
  if (!path) return ''
  if (/^https?:\/\//i.test(path)) return path

  const origin = getApiOrigin()
  const resolvedPath = resolveApiPath(path)

  if (!origin) return resolvedPath
  if (resolvedPath.startsWith('/')) return `${origin}${resolvedPath}`
  return `${origin}/${resolvedPath}`
}

export const resolveCustomerApiUrl = (path: string): string => {
  if (!path) return ''
  if (/^https?:\/\//i.test(path)) return path

  const origin = getCustomerApiBaseUrl() || getApiOrigin()
  const resolvedPath = resolveCustomerApiPath(path)

  if (!origin) return resolvedPath
  if (resolvedPath.startsWith('/')) return `${origin}${resolvedPath}`
  return `${origin}/${resolvedPath}`
}

export const resolveWarehouseApiUrl = (
  warehouse: Pick<WarehouseEndpoint, 'apiBaseUrl' | 'apiPathPrefix'>,
  path: string
): string => {
  if (!path) return ''
  if (/^https?:\/\//i.test(path)) return path

  const resolvedPath =
    path === '/api' || path.startsWith('/api/')
      ? path.replace(/^\/api(?=\/|$)/, warehouse.apiPathPrefix)
      : path

  if (!warehouse.apiBaseUrl) {
    return resolvedPath
  }

  if (resolvedPath.startsWith('/')) {
    return `${warehouse.apiBaseUrl}${resolvedPath}`
  }

  return `${warehouse.apiBaseUrl}/${resolvedPath}`
}

export const appendWarehouseQueryParam = (
  path: string,
  warehouseId: string | null | undefined
): string => {
  if (!path || !warehouseId) {
    return path
  }

  const [pathWithQuery, hash = ''] = path.split('#')
  const [pathname, rawQuery = ''] = pathWithQuery.split('?')
  const params = new URLSearchParams(rawQuery)
  Array.from(params.entries()).forEach(([key, value]) => {
    if (!value.trim()) {
      params.delete(key)
    }
  })
  params.set(GEO_QUERY_PARAM, warehouseId)
  params.delete(LEGACY_WAREHOUSE_QUERY_PARAM)
  const nextQuery = params.toString()

  return `${pathname}${nextQuery ? `?${nextQuery}` : ''}${hash ? `#${hash}` : ''}`
}

export const isExternalUrl = (value: string): boolean => {
  if (!value) return false
  if (/^(mailto|tel|sms|callto|whatsapp|tg|javascript):/i.test(value)) return true

  try {
    const url = new URL(value, typeof window !== 'undefined' ? window.location.origin : undefined)
    if (typeof window === 'undefined') return /^https?:\/\//i.test(value)
    return url.origin !== window.location.origin
  } catch {
    return false
  }
}

export const isAppRouteUrl = (value: string): boolean => {
  if (!value || isExternalUrl(value)) return false

  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
    const url = new URL(value, base)
    return !/^\/(?:api|spb-api|ekb-api)(?:\/|$)/i.test(url.pathname)
  } catch {
    return !/^(?:\/)?(?:api|spb-api|ekb-api)(?:\/|$)/i.test(value)
  }
}

export const appendWarehouseQueryToInternalUrl = (
  value: string,
  warehouseId: string | null | undefined
): string => {
  if (!value || !warehouseId || !isAppRouteUrl(value)) {
    return value
  }

  try {
    const base = typeof window !== 'undefined' ? window.location.href : 'http://localhost'
    const url = new URL(value, base)
    url.searchParams.set(GEO_QUERY_PARAM, warehouseId)
    url.searchParams.delete(LEGACY_WAREHOUSE_QUERY_PARAM)

    const relative = `${url.pathname}${url.search}${url.hash}`
    return value.startsWith(url.origin) ? url.toString() : relative
  } catch {
    return appendWarehouseQueryParam(value, warehouseId)
  }
}

export const stripWarehouseSelectionQueryParams = (search: string): string => {
  if (!search) {
    return ''
  }

  const rawSearch = search.startsWith('?') ? search.slice(1) : search
  const params = new URLSearchParams(rawSearch)
  params.delete(GEO_QUERY_PARAM)
  params.delete(LEGACY_WAREHOUSE_QUERY_PARAM)

  const nextSearch = params.toString()
  return nextSearch ? `?${nextSearch}` : ''
}

export const buildWarehouseScopedStorageKey = (
  baseKey: string,
  warehouseId: string = getCurrentWarehouseId()
): string => `${baseKey}:${warehouseId}`
